/**
 * Generic custom-field infrastructure: the field-definition shape and the pure value validator.
 *
 * ADR-style note (AST-12 / PRF-8). Asset custom fields and Performance review questions share ONE
 * infrastructure layer - definition shape, type, validation, ordering, required flag, options,
 * active state, version - but never one domain schema. Each consumer composes its own contract on
 * top (Assets add category association and searchable/filterable behaviour; Reviews add reviewer
 * visibility and weights).
 *
 * Performance's branch carries the same idea as the `@hrms/custom-fields` package (commit
 * 1b54c8a3) with the types `rating | text | single_choice | multiple_choice`. The Asset stream
 * needs `number` and `date` as well and cannot depend on a package that does not exist on this
 * branch, so this module uses the identical field names (`type`, `label`, `required`, `options`
 * with `{ value, label, sortOrder }`, `maxLength`, `isActive`, `version`) and the same
 * `validateCustomFieldValue(definition, value) -> { valid, error? }` signature. Converging is a
 * mechanical step: union the type lists, move this module into that package, re-export from here.
 *
 * Pure, no I/O, no Node built-ins: runs identically in the API, web and mobile (RN-safe).
 * Values travel as strings everywhere (number = decimal string, date = YYYY-MM-DD) so a number is
 * never a JS float (CLAUDE.md invariant 2).
 */

export const GENERIC_FIELD_TYPES = ['text', 'number', 'date', 'single_choice'] as const;
export type GenericFieldType = (typeof GENERIC_FIELD_TYPES)[number];

export interface CustomFieldOption {
  /** Stable stored identifier - never reused for a different meaning once a value references it. */
  value: string;
  /** Display text, free to edit without invalidating stored values. */
  label: string;
  sortOrder: number;
}

/** The subset the validator needs; a consumer's richer type passes straight through. */
export interface CustomFieldDefinitionLike {
  type: GenericFieldType;
  label: string;
  required: boolean;
  /** `single_choice` only. */
  options: readonly CustomFieldOption[] | null;
  /** `text` only. */
  maxLength: number | null;
}

export interface CustomFieldValidationResult {
  valid: boolean;
  /** Names the field by label; safe to show in a form. Present only when `valid` is false. */
  error?: string;
}
